import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { ReadingMarkdown } from '../editor/reading.jsx';
import './print.css';

/**
 * "Export to PDF" via the browser's native print-to-PDF. When `printNoteId` is
 * set, we render the note's reading view into a print-only portal (outside the
 * app root), wait for images/fonts, then call window.print() — the user picks
 * "Save as PDF" (works on macOS + iOS, offline, selectable text, zero deps).
 */
export function NotePrinter() {
  const printNoteId = useApp((s) => s.printNoteId);
  const setPrintNoteId = useApp((s) => s.setPrintNoteId);

  // A print-only container appended OUTSIDE #root, so @media print can hide the
  // app shell and show only this.
  const [printRoot] = useState(() => {
    const el = document.createElement('div');
    el.className = 'print-root';
    return el;
  });
  useEffect(() => {
    document.body.appendChild(printRoot);
    return () => {
      printRoot.remove();
    };
  }, [printRoot]);

  // Once the note has rendered into the portal, wait for its images (and fonts)
  // to settle, then trigger the print dialog. Clear state when it closes.
  useEffect(() => {
    if (!printNoteId) return;
    let printed = false;
    const afterPrint = () => setPrintNoteId(null);
    window.addEventListener('afterprint', afterPrint);

    const doPrint = () => {
      if (printed) return;
      printed = true;
      window.print();
    };
    const imgs = Array.from(printRoot.querySelectorAll('img'));
    const pending = imgs
      .filter((i) => !i.complete)
      .map(
        (i) =>
          new Promise<void>((res) => {
            i.addEventListener('load', () => res(), { once: true });
            i.addEventListener('error', () => res(), { once: true });
          }),
      );
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    const hardTimeout = setTimeout(doPrint, 3000); // never hang on a stuck image
    void Promise.all([fontsReady, ...pending]).then(() => setTimeout(doPrint, 100));

    return () => {
      clearTimeout(hardTimeout);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [printNoteId, printRoot, setPrintNoteId]);

  if (!printNoteId) return null;
  const entry = vault.getEntry(printNoteId);
  if (!entry || entry.kind !== 'note') return null;
  const content = vault.noteContent(printNoteId);
  const title = vault.titleOf(printNoteId);

  return createPortal(
    <article className="reading-view print-note">
      <h1 className="print-title">{title}</h1>
      <div className="reading-body">
        <ReadingMarkdown md={content} sourceId={printNoteId} onNavigate={() => {}} depth={0} />
      </div>
    </article>,
    printRoot,
  );
}

/** Open the browser print dialog for a note (Save as PDF). */
export function exportNoteToPdf(noteId: string): void {
  useApp.getState().setPrintNoteId(noteId);
}
